import React, { useState } from 'react';
import {
  ButtonGroup,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddCircleIcon from '../Icons/ModifiedMUIIcons/AddCircle';
import RemoveCircleIcon from '../Icons/ModifiedMUIIcons/RemoveCircle';
import AlertDialog from '../AlertDialogue';

type AddRemoveMealProps = {
  editCartFunctions: {
    addMealToCart: (variantId: number) => void;
    removeMealFromCart: (variantId: number) => void;
  };
  quantityInCart: number;
  variantId: number;
  soldOut: boolean;
  // This prop controls whether the minus sign and quantity show when the quantity is 0
  showRemoveWithZero: boolean;
  // This prop is optionally provided to size the buttons for certain mobile screens
  buttonSize?: string;
  fontSize?: string;
  disableRipple?: boolean;
  quantityWidth?: string;
};

const AddRemoveMeal: React.FC<AddRemoveMealProps> = ({
  editCartFunctions,
  quantityInCart,
  variantId,
  soldOut,
  showRemoveWithZero,
  buttonSize,
  disableRipple,
  fontSize,
  quantityWidth,
}) => {
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);

  // Only show the tooltip if the meal is sold out and the quantity in cart > 0
  const showTooltip = soldOut && quantityInCart > 0;

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        confirmButton={{
          fn: () => {
            editCartFunctions?.removeMealFromCart(variantId);
            setDialogOpen(false);
          },
          copy: 'YES, REMOVE',
        }}
        cancelButton={{ fn: () => setDialogOpen(false), copy: 'NEVERMIND' }}
        handleClose={() => setDialogOpen(false)}
        dialogueTitle="Are you sure?"
        dialogueContent="Once you remove this item from your order, you won't be able to add it back during this delivery week due to limited quantity."
      />
      <ButtonGroup
        disableElevation
        size="small"
        variant="contained"
        sx={{ marginLeft: '0 !important' }}
      >
        {showRemoveWithZero || quantityInCart > 0 ? (
          <>
            <IconButton
              onClick={(e) => {
                if (soldOut) {
                  setDialogOpen(true);
                } else {
                  editCartFunctions?.removeMealFromCart &&
                    editCartFunctions.removeMealFromCart(variantId);
                }

                e.stopPropagation();
              }}
              disableRipple={disableRipple}
              sx={{ p: 0 }}
            >
              <RemoveCircleIcon
                color={theme.palette.primary.main}
                style={{
                  fontSize: buttonSize ? buttonSize : '2.2rem',
                }}
                backgroundColor={theme.palette.primary.contrastText}
              />
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: quantityWidth ? 0 : '10px',
                width: quantityWidth,
                fontSize: fontSize ?? (buttonSize ? buttonSize : undefined),
              }}
            >
              {quantityInCart}
            </Typography>
          </>
        ) : null}
        <Tooltip
          title="You've got the final one"
          placement="top"
          enterDelay={0}
          enterTouchDelay={0}
          leaveDelay={500}
          leaveTouchDelay={500}
          disableHoverListener={!showTooltip}
          disableTouchListener={!showTooltip}
          followCursor={true}
        >
          <IconButton
            color="primary"
            onClick={(e) => {
              !soldOut &&
                editCartFunctions?.addMealToCart &&
                editCartFunctions.addMealToCart(variantId);
              e.stopPropagation();
            }}
            disableRipple={soldOut || disableRipple}
            sx={{ p: 0 }}
          >
            <AddCircleIcon
              color={
                !soldOut ? theme.palette.primary.main : theme.palette.grey[500]
              }
              style={{
                fontSize: buttonSize ? buttonSize : '2.2rem',
              }}
              backgroundColor={theme.palette.primary.contrastText}
              cursor={soldOut ? 'default' : 'pointer'}
            />
          </IconButton>
        </Tooltip>
      </ButtonGroup>
    </>
  );
};

export default AddRemoveMeal;
